/**
 * Why the programme's content cannot be edited, in a short form for the
 * header and a full sentence for its tooltip or an alert.
 *
 * @module router/programme-editor/editor-availability
 */

export interface EditorAvailability {
  short: string;
  full: string;
}

/** How content reopens from a read-only state, where it can. */
const WAY_BACK_TO_EDITING: Partial<Record<string, string>> = {
  "Awaiting Internal Review": "It reopens for editing if reviewers request changes.",
  "Internal Review": "It reopens for editing if reviewers request changes.",
  "Awaiting QA Approval": "It reopens for editing if changes are requested.",
  "Exported and Locked": "To change it, use Create editable revision in Governance.",
};

/** Structural sections only: the lease covers the programme's structure alone. */
export const LEASE_HELD_ELSEWHERE: EditorAvailability = {
  short: "Structure locked by another editor",
  full: "Another editor is changing the programme structure. This section is read-only until their editing lease is released or expires. Module and narrative sections are not affected.",
};

/** The programme was opened with View rather than Edit. */
export const OPENED_TO_VIEW: EditorAvailability = {
  short: "Viewing",
  full: "Opened to view, so nothing here can be changed. Choose Edit on the Programmes screen to make changes.",
};

/** The reason the account may not edit at all, whatever the workflow state. */
export const VIEW_ONLY_ROLE: EditorAvailability = {
  short: "View only",
  full: "This account can view the programme and its governance record. Editing the programme requires an assigned Programme Lead role.",
};

/**
 * The reason content is read-only, chosen by whichever applies first: a
 * workflow state that blocks edits, a missing role, then opening it to view.
 * Null when editable.
 */
export function editorAvailability({
  readOnly,
  workflowState,
  canEditWorkflowState,
  canEditProgramme,
  viewing = false,
}: {
  readOnly: boolean;
  workflowState: string | undefined;
  canEditWorkflowState: boolean;
  canEditProgramme: boolean;
  viewing?: boolean;
}): EditorAvailability | null {
  if (workflowState !== undefined && !canEditWorkflowState) {
    const wayBack = WAY_BACK_TO_EDITING[workflowState];
    return {
      short: `Read-only while ${workflowState}`,
      full: `Programme content is read-only while in ${workflowState}.${wayBack ? ` ${wayBack}` : ""}`,
    };
  }
  if (workflowState !== undefined && !canEditProgramme) {
    return VIEW_ONLY_ROLE;
  }
  if (viewing) {
    return OPENED_TO_VIEW;
  }
  if (readOnly) {
    return { short: "Read-only", full: "This programme is open read-only." };
  }
  return null;
}
